import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Shield, ChevronRight, CheckCircle, Package, CreditCard, Lock } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCustomer } from '../context/CustomerContext';
import FflDealerModal from '../components/FflDealerModal';

const BASE = process.env.REACT_APP_BACKEND_URL;
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

// Generate a random looking Visa test card
function genFakeCard() {
  const num = '4' + Array.from({length: 15}, () => Math.floor(Math.random() * 10)).join('');
  const formatted = num.match(/.{1,4}/g).join(' ');
  const yr = (new Date().getFullYear() + 2 + Math.floor(Math.random() * 3)) % 100;
  const mo = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0');
  const cvv = String(Math.floor(Math.random() * 900) + 100);
  return { number: formatted, expiry: `${mo}/${yr}`, cvv };
}

// ── Step indicator ────────────────────────────────────────────────────────────
function StepBar({ step }) {
  const steps = [
    { n: 1, label: 'Contact Info' },
    { n: 2, label: 'FFL Dealer' },
    { n: 3, label: 'Payment' },
  ];
  return (
    <div className="flex items-center gap-0 mb-8">
      {steps.map((s, i) => (
        <div key={s.n} className="flex items-center gap-0 flex-1">
          <div className={`flex flex-col items-center gap-1 flex-1 ${i < steps.length - 1 ? '' : ''}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bebas text-base transition-colors ${
              step > s.n ? 'bg-orange-500 text-white' : step === s.n ? 'bg-orange-500 text-white ring-4 ring-orange-500/30' : 'bg-zinc-800 text-zinc-500'
            }`}>
              {step > s.n ? <CheckCircle size={16} /> : s.n}
            </div>
            <span className={`font-inter text-[10px] uppercase tracking-wider ${step >= s.n ? 'text-orange-400' : 'text-zinc-600'}`}>{s.label}</span>
          </div>
          {i < steps.length - 1 && <div className={`h-px flex-1 mb-4 ${step > s.n ? 'bg-orange-500/50' : 'bg-zinc-800'}`} />}
        </div>
      ))}
    </div>
  );
}

// ── Order Summary sidebar ─────────────────────────────────────────────────────
function OrderSummary({ cartItems, cartTotal, fflDealer }) {
  const freeShipping = cartTotal >= 450;
  return (
    <div className="bg-[#1a1a1a] border border-white/10 p-5 sticky top-6">
      <h3 className="font-bebas text-lg text-white tracking-wider mb-4">ORDER SUMMARY</h3>
      <div className="space-y-3 mb-4">
        {cartItems.map(item => (
          <div key={item.key} className="flex gap-3">
            <div className="w-10 h-10 bg-[#0f0f0f] flex-shrink-0 overflow-hidden">
              <img src={item.product.image} alt="" className="w-full h-full object-contain p-1" onError={e => { e.target.style.opacity = '0.2'; }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-inter text-xs text-white leading-tight">{item.product.name}</p>
              {(item.product.nfaItem || item.product.nfa_item) && <span className="font-inter text-[9px] text-orange-400">NFA</span>}
            </div>
            <span className="font-inter text-xs text-white whitespace-nowrap">×{item.quantity} ${(item.product.price * item.quantity).toFixed(2)}</span>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10 pt-3 space-y-1.5">
        <div className="flex justify-between font-inter text-xs text-zinc-400">
          <span>Subtotal</span><span>${cartTotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-inter text-xs text-zinc-400">
          <span>Shipping</span><span className={freeShipping ? 'text-green-400' : 'text-zinc-400'}>{freeShipping ? 'FREE' : 'Calculated later'}</span>
        </div>
        <div className="flex justify-between font-bebas text-lg text-white pt-1.5 border-t border-white/10">
          <span>Total</span><span>${cartTotal.toFixed(2)}</span>
        </div>
      </div>
      {fflDealer?.business_name && (
        <div className="mt-4 pt-3 border-t border-white/10">
          <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">FFL Transfer Dealer</p>
          <p className="font-inter text-xs text-white">{fflDealer.business_name}</p>
          <p className="font-inter text-[10px] text-zinc-500">{fflDealer.city}, {fflDealer.state}</p>
        </div>
      )}
    </div>
  );
}

// ── Main Checkout Page ─────────────────────────────────────────────────────────
export default function Checkout() {
  const { cartItems, cartTotal, clearCart } = useCart();
  const { customer, token, loading: customerLoading } = useCustomer();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(1);
  const [fflModalOpen, setFflModalOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // Demo card — pre-populated with fake test numbers, editable
  const [card, setCard] = useState(() => genFakeCard());
  const setC = k => e => setCard(p => ({ ...p, [k]: e.target.value }));

  // Auth guard — redirect to login if not authenticated (after loading completes)
  useEffect(() => {
    if (!customerLoading && !customer && !location.search.includes('session_id')) {
      navigate(`/account/login?redirect=/checkout`);
    }
  }, [customerLoading, customer, navigate, location.search]);

  // hasNfa handles both camelCase (static data) and snake_case (API response)
  const hasNfa = cartItems.some(i => i.product.nfaItem || i.product.nfa_item);

  const [info, setInfo] = useState({
    first_name: customer?.first_name || '', last_name: customer?.last_name || '',
    email: customer?.email || '', phone: customer?.phone || '',
    address: customer?.shipping_address?.address || '',
    city: customer?.shipping_address?.city || '',
    state: customer?.shipping_address?.state || '',
    zip: customer?.shipping_address?.zip || '',
  });

  const [fflDealer, setFflDealer] = useState(customer?.preferred_ffl_dealer || null);

  // Pre-fill from customer profile when loaded
  useEffect(() => {
    if (customer) {
      setInfo(p => ({
        ...p,
        first_name: p.first_name || customer.first_name || '',
        last_name: p.last_name || customer.last_name || '',
        email: p.email || customer.email || '',
        phone: p.phone || customer.phone || '',
        address: p.address || customer.shipping_address?.address || '',
        city: p.city || customer.shipping_address?.city || '',
        state: p.state || customer.shipping_address?.state || '',
        zip: p.zip || customer.shipping_address?.zip || '',
      }));
      if (!fflDealer && customer.preferred_ffl_dealer?.business_name) {
        setFflDealer(customer.preferred_ffl_dealer);
      }
    }
  }, [customer]);

  // Handle return from Stripe (success/cancel)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (sessionId && token) {
      pollPaymentStatus(sessionId);
    }
  }, [location.search, token]);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    if (attempt > 8) { setError('Payment status check timed out. If you were charged, contact us.'); return; }
    try {
      const res = await fetch(`${BASE}/api/customer/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.payment_status === 'paid' && data.order_number) {
        clearCart();
        navigate(`/checkout/success?order=${data.order_number}&id=${data.order_id}`);
        return;
      }
      if (data.status === 'expired') { setError('Payment session expired. Please try again.'); return; }
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    } catch (e) { setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000); }
  };

  // Empty cart guard
  if (cartItems.length === 0 && !location.search.includes('session_id')) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
        <div className="text-center">
          <Package size={48} className="text-zinc-700 mx-auto mb-4" />
          <p className="font-bebas text-2xl text-zinc-500 tracking-wider mb-2">YOUR CART IS EMPTY</p>
          <Link to="/shop" className="font-bebas text-base tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 transition-colors inline-block mt-2">SHOP NOW →</Link>
        </div>
      </div>
    );
  }

  const setI = k => e => setInfo(p => ({ ...p, [k]: e.target.value }));
  const inputCls = "w-full bg-[#1a1a1a] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600";
  const labelCls = "block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1.5";

  const handleProceedToFfl = (e) => {
    e.preventDefault();
    if (!hasNfa) { setStep(3); return; } // skip FFL if no NFA items
    setStep(2);
  };

  const handleProceedToPayment = () => {
    if (hasNfa && !fflDealer?.business_name) {
      setError('Please select an FFL dealer for your NFA items');
      return;
    }
    setError('');
    setStep(3);
  };

  const handlePayment = async () => {
    if (!token) { navigate('/account/login'); return; }
    setProcessing(true); setError('');
    try {
      const res = await fetch(`${BASE}/api/customer/checkout/mock-pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          items: cartItems.map(i => ({
            product_slug: i.product.id || i.product._id || i.product.slug,
            product_name: i.product.name,
            product_image: i.product.image || '',
            quantity: i.quantity,
            unit_price: i.product.price,
            nfa_item: !!(i.product.nfaItem || i.product.nfa_item),
          })),
          customer_info: { first_name: info.first_name, last_name: info.last_name, email: info.email, phone: info.phone },
          shipping_address: { address: info.address, city: info.city, state: info.state, zip: info.zip },
          ffl_dealer: hasNfa ? fflDealer : null,
          notes: '',
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Payment failed');
      clearCart();
      navigate(`/checkout/success?order=${data.order_number}&id=${data.order_id}`);
    } catch (err) { setError(err.message); }
    setProcessing(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="font-bebas text-xl text-white tracking-widest hover:text-orange-400 transition-colors">
            WITT MACHINE
          </Link>
          <span className="font-bebas text-xl text-zinc-500 tracking-wider">CHECKOUT</span>
        </div>

        {!token && (
          <div className="bg-[#1a1a1a] border border-orange-500/20 p-4 mb-6 flex items-center justify-between">
            <p className="font-inter text-sm text-zinc-300">Have an account? Sign in to pre-fill your info and track your order.</p>
            <Link to={`/account/login`} className="font-bebas text-sm tracking-widest text-orange-400 hover:text-orange-300 border border-orange-500/30 px-3 py-1.5 transition-colors whitespace-nowrap ml-4">SIGN IN →</Link>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main checkout flow */}
          <div className="lg:col-span-2">
            <StepBar step={step} />

            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-5">{error}</div>}

            {/* STEP 1: Contact Info */}
            {step === 1 && (
              <form onSubmit={handleProceedToFfl} className="space-y-4">
                <div className="bg-[#1a1a1a] border border-white/10 p-5">
                  <h3 className="font-bebas text-lg text-white tracking-wider mb-4">CONTACT INFORMATION</h3>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><label className={labelCls}>First Name *</label><input required value={info.first_name} onChange={setI('first_name')} className={inputCls} placeholder="John" /></div>
                    <div><label className={labelCls}>Last Name *</label><input required value={info.last_name} onChange={setI('last_name')} className={inputCls} placeholder="Doe" /></div>
                  </div>
                  <div className="mb-3"><label className={labelCls}>Email Address *</label><input type="email" required value={info.email} onChange={setI('email')} className={inputCls} placeholder="you@example.com" /></div>
                  <div><label className={labelCls}>Phone *</label><input required value={info.phone} onChange={setI('phone')} className={inputCls} placeholder="(555) 555-5555" /></div>
                </div>

                <div className="bg-[#1a1a1a] border border-white/10 p-5">
                  <h3 className="font-bebas text-lg text-white tracking-wider mb-4">
                    PERSONAL ADDRESS
                  </h3>
                  {hasNfa && (
                    <div className="bg-orange-500/10 border border-orange-500/20 p-3 mb-4">
                      <p className="font-inter text-xs text-orange-300">
                        NFA suppressors ship to your FFL dealer — not this address. We collect your personal address for identity and billing verification only.
                      </p>
                    </div>
                  )}
                  <div className="space-y-3">
                    <div><label className={labelCls}>Street Address *</label><input required value={info.address} onChange={setI('address')} className={inputCls} placeholder="123 Main St" /></div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-1"><label className={labelCls}>City *</label><input required value={info.city} onChange={setI('city')} className={inputCls} placeholder="Austin" /></div>
                      <div>
                        <label className={labelCls}>State *</label>
                        <select required value={info.state} onChange={setI('state')} className={inputCls}>
                          <option value="">—</option>
                          {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div><label className={labelCls}>Zip *</label><input required value={info.zip} onChange={setI('zip')} className={inputCls} placeholder="78701" /></div>
                    </div>
                  </div>
                </div>

                <button type="submit" className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-xl tracking-widest py-4 transition-colors">
                  {hasNfa ? 'CONTINUE — SELECT FFL DEALER →' : 'CONTINUE TO PAYMENT'}
                </button>
              </form>
            )}

            {/* STEP 2: FFL Dealer (NFA only) */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="bg-[#1a1a1a] border border-white/10 p-5">
                  <h3 className="font-bebas text-lg text-white tracking-wider mb-2 flex items-center gap-2">
                    <Shield size={18} className="text-orange-500" />FFL DEALER SELECTION
                  </h3>
                  <p className="font-inter text-sm text-zinc-400 mb-5 leading-relaxed">
                    Your suppressor must be transferred through a licensed Class III SOT FFL dealer.
                    The suppressor ships to the dealer — you complete the ATF Form 4 there.
                  </p>

                  {fflDealer?.business_name ? (
                    <div className="flex items-start justify-between bg-green-500/10 border border-green-500/30 p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle size={18} className="text-green-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-inter text-sm text-white font-semibold">{fflDealer.business_name}</p>
                          <p className="font-inter text-xs text-zinc-400">
                            {[fflDealer.address, fflDealer.city, fflDealer.state, fflDealer.zip].filter(Boolean).join(', ')}
                          </p>
                          {fflDealer.license_number && <p className="font-inter text-[10px] text-zinc-500 mt-0.5">FFL: {fflDealer.license_number}</p>}
                        </div>
                      </div>
                      <button onClick={() => setFflModalOpen(true)}
                        className="font-inter text-xs text-orange-400 hover:text-orange-300 border border-orange-500/30 px-3 py-1.5 transition-colors whitespace-nowrap ml-3">
                        Change
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-8 border border-dashed border-white/10 mb-4">
                      <Shield size={32} className="text-zinc-700 mx-auto mb-2" />
                      <p className="font-inter text-sm text-zinc-400 mb-4">No FFL dealer selected yet</p>
                      <button onClick={() => setFflModalOpen(true)}
                        data-testid="open-ffl-modal"
                        className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-base tracking-widest px-6 py-3 transition-colors mx-auto">
                        FIND FFL DEALER NEAR ME
                      </button>
                    </div>
                  )}

                  <div className="bg-[#0f0f0f] border border-white/5 p-3">
                    <p className="font-inter text-xs text-zinc-600 leading-relaxed">
                      Only SOT-licensed (Special Occupational Taxpayer) dealers appear in the search.
                      These are dealers authorized to transfer NFA items including suppressors.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setStep(1)} className="flex-1 border border-white/20 text-zinc-400 hover:text-white font-bebas text-lg tracking-widest py-3 transition-colors">
                    ← BACK
                  </button>
                  <button
                    onClick={handleProceedToPayment}
                    disabled={!fflDealer?.business_name}
                    className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bebas text-lg tracking-widest py-3 transition-colors"
                  >
                    CONTINUE TO PAYMENT <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: Payment — Demo Card Form */}
            {step === 3 && (
              <div className="space-y-4">
                {/* Order review summary */}
                <div className="bg-[#1a1a1a] border border-white/10 p-5">
                  <h3 className="font-bebas text-lg text-white tracking-wider mb-4 flex items-center gap-2">
                    <CreditCard size={18} className="text-orange-500" />PAYMENT
                  </h3>
                  <div className="space-y-2 mb-5 text-sm font-inter">
                    <div className="flex items-start gap-2">
                      <CheckCircle size={13} className="text-orange-400 flex-shrink-0 mt-0.5" />
                      <p className="text-zinc-300"><span className="text-zinc-500 uppercase text-[10px] tracking-wider mr-2">Contact</span>{info.first_name} {info.last_name} · {info.email}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle size={13} className="text-orange-400 flex-shrink-0 mt-0.5" />
                      <p className="text-zinc-300"><span className="text-zinc-500 uppercase text-[10px] tracking-wider mr-2">Address</span>{[info.address, info.city, info.state, info.zip].filter(Boolean).join(', ')}</p>
                    </div>
                    {fflDealer?.business_name && (
                      <div className="flex items-start gap-2">
                        <CheckCircle size={13} className="text-orange-400 flex-shrink-0 mt-0.5" />
                        <p className="text-zinc-300"><span className="text-zinc-500 uppercase text-[10px] tracking-wider mr-2">FFL Dealer</span>{fflDealer.business_name} · {fflDealer.city}, {fflDealer.state}</p>
                      </div>
                    )}
                  </div>

                  {/* Demo mode notice */}
                  <div className="bg-zinc-800/60 border border-zinc-700 px-4 py-2.5 mb-5 flex items-center gap-2">
                    <span className="font-inter text-[10px] font-bold text-zinc-400 uppercase tracking-widest">TEST MODE</span>
                    <span className="font-inter text-xs text-zinc-500">— Stripe integration coming at go-live. Test card pre-populated below.</span>
                  </div>

                  {/* Mock card form */}
                  <div className="bg-[#0f0f0f] border border-white/10 p-5 mb-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Lock size={13} className="text-zinc-500" />
                        <span className="font-inter text-xs text-zinc-500 uppercase tracking-wider">Card Details</span>
                      </div>
                      <div className="flex gap-1.5">
                        {['Visa','MC','Amex'].map(b => (
                          <span key={b} className="font-inter text-[9px] text-zinc-500 border border-zinc-700 px-2 py-0.5">{b}</span>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {/* Card number */}
                      <div>
                        <label className="block font-inter text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Card Number</label>
                        <input
                          data-testid="card-number"
                          type="text"
                          value={card.number}
                          onChange={setC('number')}
                          maxLength={19}
                          className="w-full bg-[#1a1a1a] border border-white/10 focus:border-orange-500/50 text-white font-mono text-sm px-4 py-3 outline-none transition-colors tracking-widest"
                          placeholder="4XXX XXXX XXXX XXXX"
                        />
                      </div>
                      {/* Name on card */}
                      <div>
                        <label className="block font-inter text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Name on Card</label>
                        <input
                          data-testid="card-name"
                          type="text"
                          defaultValue={`${info.first_name} ${info.last_name}`.trim()}
                          className="w-full bg-[#1a1a1a] border border-white/10 focus:border-orange-500/50 text-white font-inter text-sm px-4 py-3 outline-none transition-colors"
                          placeholder="Cardholder Name"
                        />
                      </div>
                      {/* Expiry + CVV */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block font-inter text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Expiry (MM/YY)</label>
                          <input
                            data-testid="card-expiry"
                            type="text"
                            value={card.expiry}
                            onChange={setC('expiry')}
                            maxLength={5}
                            className="w-full bg-[#1a1a1a] border border-white/10 focus:border-orange-500/50 text-white font-mono text-sm px-4 py-3 outline-none transition-colors"
                            placeholder="MM/YY"
                          />
                        </div>
                        <div>
                          <label className="block font-inter text-[10px] text-zinc-600 uppercase tracking-wider mb-1">CVV</label>
                          <input
                            data-testid="card-cvv"
                            type="text"
                            value={card.cvv}
                            onChange={setC('cvv')}
                            maxLength={4}
                            className="w-full bg-[#1a1a1a] border border-white/10 focus:border-orange-500/50 text-white font-mono text-sm px-4 py-3 outline-none transition-colors"
                            placeholder="CVV"
                          />
                        </div>
                      </div>
                    </div>

                    <p className="font-inter text-[10px] text-zinc-700 mt-3">
                      All test card numbers are automatically approved. No real charges are made.
                    </p>
                  </div>

                  {hasNfa && (
                    <div className="bg-orange-500/10 border border-orange-500/30 p-3 mb-4">
                      <p className="font-inter text-xs text-orange-300 leading-relaxed">
                        <strong>NFA Notice:</strong> By placing this order you acknowledge that NFA rules apply,
                        that an ATF Form 4 transfer is required, and that this item will
                        ship to your selected FFL dealer for the transfer process.
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-between font-bebas text-2xl text-white">
                    <span>Total Due</span>
                    <span className="text-orange-400">${cartTotal.toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setStep(hasNfa ? 2 : 1)} className="flex-1 border border-white/20 text-zinc-400 hover:text-white font-bebas text-lg tracking-widest py-3 transition-colors">
                    ← BACK
                  </button>
                  <button
                    data-testid="proceed-to-payment-btn"
                    onClick={handlePayment}
                    disabled={processing}
                    className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-lg tracking-widest py-3 transition-colors"
                  >
                    <CreditCard size={18} />
                    {processing ? 'PROCESSING...' : `PLACE ORDER — $${cartTotal.toFixed(2)}`}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <OrderSummary cartItems={cartItems} cartTotal={cartTotal} fflDealer={fflDealer} />
          </div>
        </div>
      </div>

      <FflDealerModal
        isOpen={fflModalOpen}
        onClose={() => setFflModalOpen(false)}
        onSelect={(dealer) => { setFflDealer(dealer); setFflModalOpen(false); }}
        selectedDealer={fflDealer}
      />
    </div>
  );
}
